import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Plus, Key, KeyRound, Trash2, Filter, Users, UsersRound, LogOut, User, Shield, ShieldCheck, Edit, Laptop, MonitorSmartphone, Timer, Hourglass, Ticket, TicketPercent, Package, PackageOpen, Monitor, RefreshCw, Clock, Ban, Send, MessageSquare, Bug, Lightbulb, AlertTriangle, Image as ImageIcon } from "lucide-react";
import UserEditModal from "@/components/UserEditModal";
import { ActionTile } from "@/components/ui/ActionTile";
import { TINT } from "@/components/ui/IconTile";
import { adminTokenStore } from "@/services/sellerTokenStore";
import Footer from "@/components/Footer";
import CouponManager from "@/components/CouponManager";
import FeedbackManager from "@/components/FeedbackManager";
import Sparkles from "@/components/Sparkles";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatBytes = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const AdminDashboardSecure = () => {
  const navigate = useNavigate();
  const [adminUser, setAdminUser] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterTier, setFilterTier] = useState("all");
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  
  // Email update state
  const [newEmail, setNewEmail] = useState("");

  const [licenseForm, setLicenseForm] = useState({
    user_email: "",
    tier: "free",
    duration_days: 15
  });

  // Device & Trial Management
  const [trials, setTrials] = useState([]);
  const [emailTrials, setEmailTrials] = useState([]);
  const [selectedLicenseForDevices, setSelectedLicenseForDevices] = useState(null);
  const [licenseDevices, setLicenseDevices] = useState([]);
  const [offlineForm, setOfflineForm] = useState({
    license_key: "",
    user_email: "",
    device_id: "",
    machine_code: ""
  });
  const [offlineToken, setOfflineToken] = useState("");

  const [userForm, setUserForm] = useState({
    email: "",
    full_name: ""
  });

  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTier, setUploadTier] = useState("free");
  const [uploadVersion, setUploadVersion] = useState("1.0.0");
  const [uploading, setUploading] = useState(false);
  const [appPackages, setAppPackages] = useState([]);
  const [storageStats, setStorageStats] = useState(null);
  
  const [accountForm, setAccountForm] = useState({
    username: "",
    email: "",
    full_name: ""
  });
  const [editingAccount, setEditingAccount] = useState(false);

  useEffect(() => {
    // Phase-5 hardening: the admin JWT lives in memory + httpOnly cookie
    // only. On page reload the memory copy is empty — silently hit
    // /auth/admin/refresh (rides the httpOnly cookie) to re-hydrate it.
    // Fall back to /admin/login only if the refresh fails.
    let cancelled = false;

    const cachedUser = (() => {
      try { return JSON.parse(localStorage.getItem("admin_user") || "null"); }
      catch (e) { return null; }
    })();

    const hydrate = async () => {
      let token = adminTokenStore.getAccessToken();
      let user = adminTokenStore.getUser() || cachedUser;
      if (!token) {
        try {
          const res = await axios.post(`${API}/auth/admin/refresh`, {}, { withCredentials: true });
          token = res.data?.access_token || null;
          user = res.data?.user || user;
          if (token) {
            adminTokenStore.setSession(token, user);
            if (user) {
              try { localStorage.setItem("admin_user", JSON.stringify(user)); } catch (_) { /* ignore */ }
            }
          }
        } catch (err) {
          // 401/network — treat as signed out.
        }
      }
      if (cancelled) return;
      if (!token || !user) {
        toast.error("Please login first");
        navigate("/admin/login");
        return;
      }
      setAdminUser(user);
      fetchAdminInfo();
      fetchLicenses();
      fetchUsers();
      fetchAppPackages();
      fetchStorageStats();
      fetchTrials();
    };

    hydrate();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTrials = async () => {
    try {
      const response = await axios.get(`${API}/admin/trials`, getAuthHeader());
      setTrials(response.data.device_trials || response.data.trials || []);
      setEmailTrials(response.data.email_trials || []);
    } catch (error) {
      // Trials endpoint might not exist yet
      console.log("Trials fetch:", error.message);
    }
  };

  const deleteEmailTrial = async (email) => {
    if (!window.confirm(`Delete trial request for ${email}? This will allow them to request a new trial.`)) return;
    try {
      await axios.delete(`${API}/admin/email-trials/${encodeURIComponent(email)}`, getAuthHeader());
      toast.success("Email trial deleted");
      fetchTrials();
    } catch (error) {
      toast.error("Failed to delete email trial");
    }
  };

  const fetchLicenseDevices = async (licenseKey, email) => {
    try {
      const response = await axios.get(`${API}/licenses/devices/${licenseKey}?email=${email}`);
      setLicenseDevices(response.data.devices || []);
      setSelectedLicenseForDevices({ license_key: licenseKey, email });
    } catch (error) {
      toast.error("Failed to fetch devices");
    }
  };

  const handleRevokeLicense = async (licenseKey) => {
    if (!window.confirm("Are you sure you want to revoke this license? This will deactivate all devices.")) return;
    try {
      await axios.post(`${API}/admin/licenses/${licenseKey}/revoke`, {}, getAuthHeader());
      toast.success("License revoked successfully");
      fetchLicenses();
    } catch (error) {
      toast.error("Failed to revoke license");
    }
  };

  const handleClearDevices = async (licenseKey) => {
    if (!window.confirm("Clear all devices from this license?")) return;
    try {
      await axios.post(`${API}/admin/licenses/${licenseKey}/clear-devices`, {}, getAuthHeader());
      toast.success("All devices cleared");
      fetchLicenses();
      setLicenseDevices([]);
    } catch (error) {
      toast.error("Failed to clear devices");
    }
  };

  const handleTransferLicense = async (licenseKey, currentEmail) => {
    const newEmail = window.prompt(
      `Transfer license to a new email address.\n\nCurrent owner: ${currentEmail}\n\nEnter the new owner's email:`,
      ""
    );
    if (!newEmail) return;

    const trimmed = newEmail.trim().toLowerCase();
    // Basic email sanity check — backend does full validation.
    if (!trimmed.includes("@") || !trimmed.includes(".")) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (trimmed === (currentEmail || "").trim().toLowerCase()) {
      toast.error("New email must differ from the current owner");
      return;
    }

    const clearDevices = window.confirm(
      `Also clear all activated devices?\n\nOK = clear devices (new owner starts fresh — recommended)\nCancel = keep existing devices activated`
    );

    try {
      await axios.post(
        `${API}/admin/licenses/${licenseKey}/transfer`,
        { new_email: trimmed, clear_devices: clearDevices },
        getAuthHeader()
      );
      toast.success(`License transferred to ${trimmed}`);
      fetchLicenses();
      setLicenseDevices([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to transfer license");
    }
  };

  const handleExtendLicense = async (licenseKey, days = 365) => {
    const daysInput = window.prompt("Enter number of days to extend:", "365");
    if (!daysInput) return;
    
    const daysToExtend = parseInt(daysInput);
    if (isNaN(daysToExtend) || daysToExtend <= 0) {
      toast.error("Please enter a valid number of days");
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/admin/licenses/${licenseKey}/extend`,
        { days: daysToExtend },
        getAuthHeader()
      );
      toast.success(`License extended by ${daysToExtend} days`);
      fetchLicenses();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to extend license");
    }
  };

  const handleGenerateOfflineToken = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await axios.post(
        `${API}/admin/licenses/generate-offline-token`,
        offlineForm,
        getAuthHeader()
      );
      setOfflineToken(response.data.offline_token);
      toast.success("Offline token generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate token");
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminInfo = async () => {
    try {
      const response = await axios.get(`${API}/auth/admin/me`, getAuthHeader());
      setAdminUser(response.data);
      setTwoFactorEnabled(response.data.two_factor_enabled || false);
      setAccountForm({
        username: response.data.username || "",
        email: response.data.email || "",
        full_name: response.data.full_name || ""
      });
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Session expired. Please login again.");
        handleLogout();
      }
    }
  };

  const handleToggle2FA = async (enabled) => {
    try {
      setLoading(true);
      await axios.patch(
        `${API}/auth/admin/toggle-2fa?enabled=${enabled}`,
        {},
        getAuthHeader()
      );
      setTwoFactorEnabled(enabled);
      toast.success(
        `Two-factor authentication ${enabled ? "enabled" : "disabled"}! Check your email for confirmation.`
      );
    } catch (error) {
      toast.error("Failed to update 2FA settings");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("New passwords don't match!");
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    try {
      setLoading(true);
      await axios.patch(
        `${API}/auth/admin/change-password`,
        {
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        },
        getAuthHeader()
      );
      
      toast.success("Password changed successfully! Check your email for confirmation.");
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    
    if (!newEmail) {
      toast.error("Please enter a new email address");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.patch(
        `${API}/auth/admin/update-email`,
        { new_email: newEmail },
        getAuthHeader()
      );
      
      toast.success("Email updated successfully! Check both emails for confirmation.");
      
      // Update local admin user data
      const updatedUser = { ...adminUser, email: response.data.new_email };
      setAdminUser(updatedUser);
      localStorage.setItem("admin_user", JSON.stringify(updatedUser));
      
      setNewEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update email");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getAuthHeader = () => {
    const token = adminTokenStore.getAccessToken();
    // withCredentials so the httpOnly cookie also rides along — if the
    // Authorization header is missing/expired, the backend will fall
    // through to the cookie automatically.
    return {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true,
    };
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/admin/logout`, {}, { withCredentials: true });
    } catch (err) {
      // Best-effort — even if the server call fails we still clear locally.
    }
    adminTokenStore.clear();
    try { localStorage.removeItem("admin_user"); } catch (_) { /* ignore */ }
    toast.success("Logged out successfully");
    navigate("/admin/login");
  };

  const fetchLicenses = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.append("status", filterStatus);
      if (filterTier !== "all") params.append("tier", filterTier);

      const response = await axios.get(`${API}/admin/licenses?${params}`, getAuthHeader());
      setLicenses(response.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Session expired. Please login again.");
        handleLogout();
      } else {
        toast.error("Failed to fetch licenses");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, getAuthHeader());
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Session expired. Please login again.");
        handleLogout();
      } else {
        toast.error("Failed to fetch users");
      }
    }
  };

  const fetchAppPackages = async () => {
    try {
      const response = await axios.get(`${API}/admin/app-packages`, getAuthHeader());
      setAppPackages(response.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Session expired. Please login again.");
        handleLogout();
      } else {
        toast.error("Failed to fetch app packages");
      }
    }
  };

  const fetchStorageStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/app-packages/storage`, getAuthHeader());
      setStorageStats(response.data);
    } catch (error) {
      // Non-fatal — storage meter is a nice-to-have.
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        // Swallow silently; the meter simply won't render.
      }
    }
  };

  const handlePruneOrphans = async () => {
    if (!window.confirm("Delete every GridFS file with no matching package row? This cannot be undone.")) return;
    try {
      const r = await axios.post(
        `${API}/admin/app-packages/prune-orphans`,
        {},
        getAuthHeader()
      );
      const { deleted_count, reclaimed_bytes } = r.data || {};
      toast.success(
        `Pruned ${deleted_count} file${deleted_count === 1 ? "" : "s"} · ${formatBytes(reclaimed_bytes || 0)} reclaimed`
      );
      fetchStorageStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to prune orphaned files");
    }
  };

  const handleCreateLicense = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(`${API}/admin/licenses`, licenseForm, getAuthHeader());
      toast.success("License created and email sent successfully!");
      setLicenseForm({ user_email: "", tier: "free", duration_days: 15 });
      fetchLicenses();
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      } else {
        toast.error(error.response?.data?.detail || "Failed to create license");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(`${API}/admin/users`, userForm, getAuthHeader());
      toast.success("User created successfully!");
      setUserForm({ email: "", full_name: "" });
      fetchUsers();
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      } else {
        toast.error(error.response?.data?.detail || "Failed to create user");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (licenseKey, currentStatus) => {
    try {
      const newStatus = currentStatus === "active" ? "inactive" : "active";
      await axios.patch(
        `${API}/admin/licenses/${licenseKey}/status`,
        { status: newStatus },
        getAuthHeader()
      );
      toast.success(`License ${newStatus === "active" ? "activated" : "deactivated"}`);
      fetchLicenses();
    } catch (error) {
      toast.error("Failed to update license status");
    }
  };

  const handleDeleteLicense = async (licenseKey) => {
    if (!window.confirm("Are you sure you want to delete this license?")) return;

    try {
      await axios.delete(`${API}/admin/licenses/${licenseKey}`, getAuthHeader());
      toast.success("License deleted successfully");
      fetchLicenses();
    } catch (error) {
      toast.error("Failed to delete license");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      await axios.delete(`${API}/admin/users/${userId}`, getAuthHeader());
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsUserModalOpen(true);
  };

  const handleSaveUser = async (userId, updatedData) => {
    try {
      setLoading(true);
      
      // Update user info (name and email)
      if (updatedData.full_name || updatedData.email) {
        await axios.patch(
          `${API}/admin/users/${userId}/update`,
          {
            full_name: updatedData.full_name,
            email: updatedData.email
          },
          getAuthHeader()
        );
      }
      
      // Update 2FA status
      const currentUser = users.find(u => u.id === userId);
      if (currentUser && currentUser.two_factor_enabled !== updatedData.two_factor_enabled) {
        await axios.patch(
          `${API}/admin/users/${userId}/toggle-2fa?enabled=${updatedData.two_factor_enabled}`,
          {},
          getAuthHeader()
        );
      }
      
      toast.success("User updated successfully!");
      setIsUserModalOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setLoading(false);
    }
  };

  const handleUploadApp = async (e) => {
    e.preventDefault();
    
    if (!uploadFile) {
      toast.error("Please select a ZIP file");
      return;
    }
    
    if (!uploadFile.name.endsWith('.zip')) {
      toast.error("Only ZIP files are allowed");
      return;
    }
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      const response = await axios.post(
        `${API}/admin/upload-app?tier=${uploadTier}&version=${uploadVersion}`,
        formData,
        {
          headers: {
            ...getAuthHeader().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success("App package uploaded successfully!");
      setUploadFile(null);
      setUploadVersion("1.0.0");
      fetchAppPackages();
      fetchStorageStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload app package");
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePackage = async (packageId) => {
    if (!window.confirm("Are you sure you want to delete this package?")) return;

    try {
      await axios.delete(`${API}/admin/app-packages/${packageId}`, getAuthHeader());
      toast.success("Package deleted successfully");
      fetchAppPackages();
      fetchStorageStats();
    } catch (error) {
      toast.error("Failed to delete package");
    }
  };

  const handleUpdateAccount = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const response = await axios.patch(
        `${API}/auth/admin/update-account`,
        accountForm,
        getAuthHeader()
      );
      
      toast.success("Account information updated successfully!");
      
      // Update local storage if username changed
      if (response.data.new_username && response.data.new_username !== adminUser.username) {
        const updatedUser = { ...adminUser, username: response.data.new_username };
        localStorage.setItem("admin_user", JSON.stringify(updatedUser));
      }
      
      setEditingAccount(false);
      fetchAdminInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update account information");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingAccount(false);
    // Reset form to current values
    setAccountForm({
      username: adminUser?.username || "",
      email: adminUser?.email || "",
      full_name: adminUser?.full_name || ""
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const getStatusBadgeClass = (status) => {
    const classes = { active: "badge-active", inactive: "badge-inactive", expired: "badge-expired" };
    return `badge ${classes[status] || ""}`;
  };

  const getTierBadgeClass = (tier) => {
    const classes = { free: "badge-free", pro: "badge-pro" };
    return `badge ${classes[tier] || ""}`;
  };

  useEffect(() => {
    if (adminUser) {
      fetchLicenses();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterTier]);

  if (!adminUser) {
    return null;
  }

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse at top, #1e6fd9 0%, #0b52b3 40%, #0a4599 100%)",
      }}
    >
      {/* Consistent app-wide sparkle background */}
      <Sparkles count={30} color="rgba(255,255,255,0.85)" minSize={6} maxSize={16} />

      {/* Header */}
      <div className="relative z-10 bg-white/95 backdrop-blur-sm border-b border-white/20 py-4">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">
            Maidly Business
          </h1>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-gray-600">Logged in as</p>
              <p className="font-semibold text-gray-800">{adminUser.username}</p>
            </div>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="px-4 py-2 border border-red-200 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium"
            >
              <LogOut className="w-4 h-4 mr-2 inline" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex-1 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Page Title */}
          <div className="mb-8">
            <button
              onClick={() => navigate("/")}
              className="text-sm text-white/80 hover:text-white mb-4 inline-flex items-center gap-1 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </button>
            <h2 className="text-3xl font-bold text-white">
              Admin Dashboard
            </h2>
            <p className="text-white/80">Manage licenses and users</p>
          </div>

        <Tabs defaultValue="licenses" className="space-y-6">
          <TabsList className="grid w-full max-w-5xl grid-cols-8 bg-white/95 backdrop-blur-sm border border-white/30 rounded-xl shadow-lg p-1.5 h-auto">
            <TabsTrigger value="licenses" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 rounded-lg text-xs py-2 flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-md bg-blue-100 flex items-center justify-center">
                <KeyRound className="w-3.5 h-3.5 text-blue-600" strokeWidth={2.5} />
              </div>
              Licenses
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 rounded-lg text-xs py-2 flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-md bg-emerald-100 flex items-center justify-center">
                <UsersRound className="w-3.5 h-3.5 text-emerald-600" strokeWidth={2.5} />
              </div>
              Users
            </TabsTrigger>
            <TabsTrigger value="devices" className="data-[state=active]:bg-violet-50 data-[state=active]:text-violet-700 rounded-lg text-xs py-2 flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-md bg-violet-100 flex items-center justify-center">
                <MonitorSmartphone className="w-3.5 h-3.5 text-violet-600" strokeWidth={2.5} />
              </div>
              Devices
            </TabsTrigger>
            <TabsTrigger value="trials" className="data-[state=active]:bg-amber-50 data-[state=active]:text-amber-700 rounded-lg text-xs py-2 flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-md bg-amber-100 flex items-center justify-center">
                <Hourglass className="w-3.5 h-3.5 text-amber-600" strokeWidth={2.5} />
              </div>
              Trials
            </TabsTrigger>
            <TabsTrigger value="coupons" className="data-[state=active]:bg-pink-50 data-[state=active]:text-pink-700 rounded-lg text-xs py-2 flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-md bg-pink-100 flex items-center justify-center">
                <TicketPercent className="w-3.5 h-3.5 text-pink-600" strokeWidth={2.5} />
              </div>
              Coupons
            </TabsTrigger>
            <TabsTrigger value="uploads" className="data-[state=active]:bg-orange-50 data-[state=active]:text-orange-700 rounded-lg text-xs py-2 flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-md bg-orange-100 flex items-center justify-center">
                <PackageOpen className="w-3.5 h-3.5 text-orange-600" strokeWidth={2.5} />
              </div>
              Uploads
            </TabsTrigger>
            <TabsTrigger value="feedback" className="data-[state=active]:bg-cyan-50 data-[state=active]:text-cyan-700 rounded-lg text-xs py-2 flex items-center gap-1.5" data-testid="tab-feedback">
              <div className="w-6 h-6 rounded-md bg-cyan-100 flex items-center justify-center">
                <MessageSquare className="w-3.5 h-3.5 text-cyan-600" strokeWidth={2.5} />
              </div>
              Feedback
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-red-50 data-[state=active]:text-red-700 rounded-lg text-xs py-2 flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-md bg-red-100 flex items-center justify-center">
                <ShieldCheck className="w-3.5 h-3.5 text-red-600" strokeWidth={2.5} />
              </div>
              Security
            </TabsTrigger>
          </TabsList>

          {/* Licenses Tab */}
          <TabsContent value="licenses">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Create License Form */}
              <Card className="bg-white border border-gray-100 shadow-lg rounded-2xl lg:col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Plus className="w-5 h-5 text-blue-600" />
                    </div>
                    Generate License
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCreateLicense} className="space-y-4">
                    <div>
                      <Label htmlFor="email">User Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={licenseForm.user_email}
                        onChange={(e) => setLicenseForm({ ...licenseForm, user_email: e.target.value })}
                        required
                        className="border-gray-300 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="tier">License Tier</Label>
                      <Select
                        value={licenseForm.tier}
                        onValueChange={(value) => {
                          const durations = { free: 15, pro: 365 };
                          setLicenseForm({ ...licenseForm, tier: value, duration_days: durations[value] });
                        }}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="free">Free (15 days)</SelectItem>
                          <SelectItem value="pro">Pro (1 year)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="duration">Duration (days)</Label>
                      <Input
                        id="duration"
                        type="number"
                        min="1"
                        value={licenseForm.duration_days}
                        onChange={(e) => setLicenseForm({ ...licenseForm, duration_days: parseInt(e.target.value) })}
                        className="border-gray-300 focus:border-blue-500"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 rounded-lg text-white font-semibold shadow-lg disabled:opacity-60 transition-all flex items-center justify-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #2196f3 0%, #1565c0 100%)",
                      }}
                    >
                      <Key className="w-4 h-4 mr-2" />
                      {loading ? "Creating..." : "Generate & Send Email"}
                    </button>
                  </form>
                </CardContent>
              </Card>

              {/* Licenses List */}
              <Card className="bg-white border border-gray-100 shadow-lg rounded-2xl lg:col-span-2">
                <CardHeader>
                  <div className="flex justify-between items-center flex-wrap gap-4">
                    <CardTitle>All Licenses ({licenses.length})</CardTitle>
                    <div className="flex gap-2 items-center">
                      <Filter className="w-4 h-4 text-gray-500" />
                      <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Status</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                          <SelectItem value="expired">Expired</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={filterTier} onValueChange={setFilterTier}>
                        <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Tiers</SelectItem>
                          <SelectItem value="free">Free</SelectItem>
                          <SelectItem value="pro">Pro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="text-center py-8 text-gray-500">Loading...</div>
                  ) : licenses.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No licenses found.</div>
                  ) : (
                    <div className="space-y-4 max-h-[600px] overflow-y-auto">
                      {licenses.map((license) => (
                        <div key={license.license_key} className="bg-gray-50 border border-gray-200 p-4 hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start gap-4 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <span className={getTierBadgeClass(license.tier)}>{license.tier}</span>
                                <span className={getStatusBadgeClass(license.status)}>{license.status}</span>
                              </div>
                              <p className="text-sm text-gray-600 mb-1"><strong>Email:</strong> {license.user_email}</p>
                              <p
                                className="text-xs font-mono bg-gray-100 p-2 rounded cursor-pointer hover:bg-gray-200 truncate"
                                onClick={() => copyToClipboard(license.license_key)}
                                title="Click to copy"
                              >
                                {license.license_key}
                              </p>
                              <p className="text-xs text-gray-500 mt-2">
                                <strong>Expires:</strong> {new Date(license.expires_at).toLocaleDateString()} ({license.days_remaining} days)
                              </p>
                            </div>
                            <div className="flex items-center gap-1 flex-wrap">
                              <Switch
                                checked={license.status === "active"}
                                onCheckedChange={() => handleToggleStatus(license.license_key, license.status)}
                                disabled={license.status === "expired" || license.status === "revoked"}
                              />
                              <ActionTile
                                icon={Monitor}
                                tint="#7c3aed"
                                onClick={() => fetchLicenseDevices(license.license_key, license.user_email)}
                                title={`View Devices (${license.devices_used || 0}/3)`}
                              />
                              <ActionTile
                                icon={RefreshCw}
                                tint={TINT.dashboard}
                                onClick={() => handleClearDevices(license.license_key)}
                                title="Clear Devices"
                              />
                              <ActionTile
                                icon={Clock}
                                tint={TINT.green}
                                onClick={() => handleExtendLicense(license.license_key)}
                                title="Extend License"
                                primary
                                label="Extend"
                              />
                              <ActionTile
                                icon={Send}
                                tint="#4f46e5"
                                onClick={() => handleTransferLicense(license.license_key, license.user_email)}
                                title="Transfer License to New Email"
                                testid={`transfer-license-btn-${license.license_key}`}
                              />
                              <ActionTile
                                icon={Ban}
                                tint="#d97706"
                                onClick={() => handleRevokeLicense(license.license_key)}
                                title="Revoke License"
                              />
                              <ActionTile
                                icon={Trash2}
                                tint={TINT.expenses}
                                onClick={() => handleDeleteLicense(license.license_key)}
                                title="Delete License"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Create User Form */}
              <Card className="bg-white border border-gray-100 shadow-lg rounded-2xl lg:col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-2xl">
                    <Plus className="w-6 h-6 text-blue-700" />
                    Add User
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCreateUser} className="space-y-4">
                    <div>
                      <Label htmlFor="user-email">Email</Label>
                      <Input
                        id="user-email"
                        type="email"
                        value={userForm.email}
                        onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="full-name">Full Name</Label>
                      <Input
                        id="full-name"
                        type="text"
                        value={userForm.full_name}
                        onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 text-white font-semibold transition-all duration-200 flex items-center justify-center"
                      style={{ backgroundColor: '#0073ea' }}
                      onMouseEnter={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#005bb5')}
                      onMouseLeave={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#0073ea')}
                    >
                      <User className="w-4 h-4 mr-2" />
                      {loading ? "Creating..." : "Add User"}
                    </button>
                  </form>
                </CardContent>
              </Card>

              {/* Users List */}
              <Card className="bg-white border border-gray-100 shadow-lg rounded-2xl lg:col-span-2">
                <CardHeader>
                  <CardTitle>All Users ({users.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {users.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No users found.</div>
                  ) : (
                    <div className="space-y-4 max-h-[600px] overflow-y-auto">
                      {users.map((user) => (
                        <div key={user.id} className="bg-gray-50 border border-gray-200 p-4 hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-semibold text-gray-800">{user.full_name}</p>
                                {user.two_factor_enabled && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                                    <Shield className="w-3 h-3" />
                                    2FA
                                  </span>
                                )}
                                {user.license_tier && (
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                                    user.license_tier === 'pro' 
                                      ? 'bg-purple-100 text-purple-700' 
                                      : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {user.license_tier.toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">{user.email}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                Created: {new Date(user.created_at).toLocaleDateString()}
                              </p>
                              {user.license_key ? (
                                <div className="mt-2 p-2 bg-green-50 rounded-md border border-green-200">
                                  <p className="text-xs text-green-700 font-medium">
                                    🔑 License: {user.license_key.substring(0, 8)}...
                                  </p>
                                  <p className="text-xs text-green-600">
                                    Status: <span className="font-semibold">{user.license_status?.toUpperCase()}</span>
                                    {user.license_expires && (
                                      <> | Expires: {new Date(user.license_expires).toLocaleDateString()}</>
                                    )}
                                  </p>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="mt-2 text-xs h-7 text-green-600 hover:bg-green-100"
                                    onClick={() => handleExtendLicense(user.license_key)}
                                  >
                                    ⏰ Extend License
                                  </Button>
                                </div>
                              ) : (
                                <div className="mt-2 flex items-center gap-2">
                                  <span className="text-xs text-blue-500">⚠️ No license</span>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-xs h-7"
                                    onClick={async () => {
                                      if (!window.confirm(`Generate PRO license for ${user.email}?`)) return;
                                      try {
                                        await axios.post(`${API}/admin/licenses`, {
                                          user_email: user.email,
                                          tier: "pro",
                                          duration_days: 365
                                        }, getAuthHeader());
                                        toast.success("License created!");
                                        fetchUsers();
                                        fetchLicenses();
                                      } catch (e) {
                                        toast.error("Failed to create license");
                                      }
                                    }}
                                  >
                                    🔑 Generate License
                                  </Button>
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <ActionTile
                                icon={Edit}
                                tint={TINT.timeClock}
                                onClick={() => handleEditUser(user)}
                                title="Edit user"
                              />
                              <ActionTile
                                icon={Trash2}
                                tint={TINT.expenses}
                                onClick={() => handleDeleteUser(user.id)}
                                title="Delete user"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Devices Tab */}
          <TabsContent value="devices">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Device Viewer */}
              <Card className="bg-white border border-gray-100 shadow-lg rounded-2xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    💻 License Devices
                  </CardTitle>
                  <p className="text-sm text-gray-500">View and manage devices for each license (max 3 per license)</p>
                </CardHeader>
                <CardContent>
                  {selectedLicenseForDevices ? (
                    <div className="space-y-4">
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <p className="text-sm"><strong>License:</strong> {selectedLicenseForDevices.license_key.substring(0, 8)}...</p>
                        <p className="text-sm"><strong>Email:</strong> {selectedLicenseForDevices.email}</p>
                      </div>
                      <div className="space-y-2">
                        <p className="font-semibold">Activated Devices ({licenseDevices.length}/3):</p>
                        {licenseDevices.length === 0 ? (
                          <p className="text-gray-500 text-sm">No devices activated</p>
                        ) : (
                          licenseDevices.map((device) => {
                            // Backward-compat: `device` may be either an
                            // object ({device_id, device_name, activated_at,
                            // last_seen}) or a plain string id from the old
                            // schema.
                            const isObj = device && typeof device === 'object';
                            const deviceId = isObj ? device.device_id : device;
                            const deviceName = isObj
                              ? (device.device_name || 'Unnamed device')
                              : 'Legacy device (name unknown)';
                            const activatedAt = isObj ? device.activated_at : null;
                            const lastSeen = isObj ? device.last_seen : null;
                            const relTime = (iso) => {
                              if (!iso) return null;
                              const d = new Date(iso);
                              const diffMs = Date.now() - d.getTime();
                              if (Number.isNaN(diffMs)) return null;
                              const mins = Math.floor(diffMs / 60000);
                              if (mins < 1) return 'just now';
                              if (mins < 60) return `${mins}m ago`;
                              const hrs = Math.floor(mins / 60);
                              if (hrs < 24) return `${hrs}h ago`;
                              const days = Math.floor(hrs / 24);
                              if (days < 30) return `${days}d ago`;
                              return d.toLocaleDateString();
                            };
                            return (
                              <div
                                key={deviceId}
                                className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                                data-testid={`device-slot-${deviceId}`}
                              >
                                <div className="flex justify-between items-start gap-3">
                                  <div className="min-w-0 flex-1">
                                    <div className="font-semibold text-sm text-gray-900 truncate">
                                      {deviceName}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      <span className="font-mono">{String(deviceId).substring(0, 16)}…</span>
                                    </div>
                                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-gray-600">
                                      {activatedAt && (
                                        <span title={new Date(activatedAt).toLocaleString()}>
                                          <strong>Activated:</strong> {relTime(activatedAt)}
                                        </span>
                                      )}
                                      {lastSeen && (
                                        <span
                                          title={new Date(lastSeen).toLocaleString()}
                                          className={
                                            (Date.now() - new Date(lastSeen).getTime()) > 30 * 86400000
                                              ? 'text-amber-600'
                                              : ''
                                          }
                                        >
                                          <strong>Last seen:</strong> {relTime(lastSeen)}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={async () => {
                                      try {
                                        await axios.post(`${API}/licenses/deactivate-device`, {
                                          license_key: selectedLicenseForDevices.license_key,
                                          user_email: selectedLicenseForDevices.email,
                                          device_id: deviceId
                                        });
                                        toast.success("Device removed");
                                        fetchLicenseDevices(selectedLicenseForDevices.license_key, selectedLicenseForDevices.email);
                                      } catch (e) {
                                        toast.error("Failed to remove device");
                                      }
                                    }}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                      <Button
                        variant="outline"
                        onClick={() => handleClearDevices(selectedLicenseForDevices.license_key)}
                        className="w-full"
                      >
                        🔄 Clear All Devices
                      </Button>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">
                      Select a license from the Licenses tab to view its devices
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Offline Activation Generator */}
              <Card className="bg-white border border-gray-100 shadow-lg rounded-2xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    🔐 Offline Activation Generator
                  </CardTitle>
                  <p className="text-sm text-gray-500">Generate RSA-signed tokens for offline activation</p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleGenerateOfflineToken} className="space-y-4">
                    <Input
                      placeholder="License Key (UUID)"
                      value={offlineForm.license_key}
                      onChange={(e) => setOfflineForm({...offlineForm, license_key: e.target.value})}
                      required
                    />
                    <Input
                      placeholder="User Email"
                      type="email"
                      value={offlineForm.user_email}
                      onChange={(e) => setOfflineForm({...offlineForm, user_email: e.target.value})}
                      required
                    />
                    <Input
                      placeholder="Device ID"
                      value={offlineForm.device_id}
                      onChange={(e) => setOfflineForm({...offlineForm, device_id: e.target.value})}
                      required
                    />
                    <Input
                      placeholder="Machine Code (Hardware ID)"
                      value={offlineForm.machine_code}
                      onChange={(e) => setOfflineForm({...offlineForm, machine_code: e.target.value})}
                      required
                    />
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? "Generating..." : "🔑 Generate Offline Token"}
                    </Button>
                  </form>
                  {offlineToken && (
                    <div className="mt-4 space-y-2">
                      <p className="font-semibold text-green-600">✅ Token Generated!</p>
                      <textarea
                        className="w-full h-32 p-2 border rounded font-mono text-xs"
                        value={offlineToken}
                        readOnly
                      />
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          navigator.clipboard.writeText(offlineToken);
                          toast.success("Token copied to clipboard!");
                        }}
                      >
                        📋 Copy Token
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Trials Tab */}
          <TabsContent value="trials">
            <div className="space-y-6">
              {/* Website Trial Requests */}
              <Card className="bg-white border border-gray-100 shadow-lg rounded-2xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    📧 Website Trial Requests
                    <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-normal">
                      {emailTrials.length} total
                    </span>
                  </CardTitle>
                  <p className="text-sm text-gray-500">Users who requested free trials from the purchase page</p>
                </CardHeader>
                <CardContent>
                  {emailTrials.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No email trial requests found</p>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {emailTrials.map((trial) => (
                        <div key={trial.id || trial.user_email} className="flex justify-between items-center p-3 bg-blue-50 rounded-lg border border-blue-100">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                              {trial.user_email?.charAt(0).toUpperCase() || "?"}
                            </div>
                            <div>
                              <p className="font-medium text-sm">{trial.user_email}</p>
                              <p className="text-xs text-gray-500">
                                Requested: {trial.requested_at ? new Date(trial.requested_at).toLocaleString() : "N/A"}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                              Free Trial
                            </span>
                            <ActionTile
                              icon={Trash2}
                              tint={TINT.expenses}
                              onClick={() => deleteEmailTrial(trial.user_email)}
                              title="Delete trial"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Device Trials */}
              <Card className="bg-white border border-gray-100 shadow-lg rounded-2xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    💻 Device Trials (15 Days)
                    <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-normal">
                      {trials.length} active
                    </span>
                  </CardTitle>
                  <p className="text-sm text-gray-500">Device-locked trial activations for desktop app</p>
                </CardHeader>
                <CardContent>
                  {trials.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No device trials found</p>
                  ) : (
                    <div className="space-y-3">
                      {trials.map((trial) => (
                        <div key={trial.id || trial.device_id} className="flex justify-between items-center p-4 bg-purple-50 rounded-lg border border-purple-100">
                          <div>
                            <p className="font-mono text-sm font-semibold">{trial.device_id}</p>
                            <p className="text-xs text-gray-500">
                              Started: {new Date(trial.started_at).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-gray-500">
                              Expires: {new Date(trial.expires_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              trial.is_active 
                                ? "bg-green-100 text-green-700" 
                                : "bg-red-100 text-red-700"
                            }`}>
                              {trial.is_active ? `${trial.days_remaining} days left` : "Expired"}
                            </span>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={async () => {
                                if (!window.confirm("Delete this trial? The device can start a new trial.")) return;
                                try {
                                  await axios.delete(`${API}/admin/trials/${trial.device_id}`, getAuthHeader());
                                  toast.success("Trial deleted");
                                  fetchTrials();
                                } catch (e) {
                                  toast.error("Failed to delete trial");
                                }
                              }}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Coupons Tab */}
          <TabsContent value="coupons">
            <CouponManager />
          </TabsContent>

          {/* Feedback Tab */}
          <TabsContent value="feedback">
            <FeedbackManager
              getAuthHeader={getAuthHeader}
              onAuthFailure={handleLogout}
            />
          </TabsContent>

          {/* Uploads Tab */}
          <TabsContent value="uploads">
            <div className="space-y-6">
              {/* Upload New Package */}
              <Card className="bg-white border border-gray-100 shadow-lg rounded-2xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-2xl">
                    📦 Upload Web App Package
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleUploadApp} className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-blue-800">
                        <strong>📝 Instructions:</strong>
                      </p>
                      <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
                        <li>Upload a ZIP file containing your web application</li>
                        <li>Customers will receive automatic download links in their license emails</li>
                        <li>Include a VERCEL_DEPLOYMENT_GUIDE.md file in the ZIP for easy deployment</li>
                        <li>Only one active package per tier (uploading new one deactivates old)</li>
                      </ul>
                    </div>

                    <div>
                      <Label htmlFor="app-file">Select ZIP File *</Label>
                      <Input
                        id="app-file"
                        type="file"
                        accept=".zip"
                        onChange={(e) => setUploadFile(e.target.files[0])}
                        required
                        className="mt-1"
                      />
                      {uploadFile && (
                        <p className="text-sm text-gray-600 mt-2">
                          Selected: {uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(2)} MB)
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="upload-tier">License Tier *</Label>
                        <Select value={uploadTier} onValueChange={setUploadTier}>
                          <SelectTrigger id="upload-tier">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">Free (Trial)</SelectItem>
                            <SelectItem value="pro">Pro</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="upload-version">Version</Label>
                        <Input
                          id="upload-version"
                          type="text"
                          value={uploadVersion}
                          onChange={(e) => setUploadVersion(e.target.value)}
                          placeholder="1.0.0"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={uploading || !uploadFile}
                      className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                    >
                      {uploading ? "Uploading..." : "📤 Upload Package"}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Storage Meter */}
              {storageStats && (
                <Card
                  className="bg-white border border-gray-100 shadow-lg rounded-2xl"
                  data-testid="app-packages-storage-meter"
                >
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-2xl">
                      💾 GridFS Storage
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-xl">
                        <p className="text-xs uppercase tracking-wide text-blue-700 font-semibold">
                          Total used
                        </p>
                        <p
                          className="text-2xl font-bold text-blue-900 mt-1"
                          data-testid="storage-total-bytes"
                        >
                          {formatBytes(storageStats.gridfs_total_bytes)}
                        </p>
                        <p className="text-xs text-blue-700/70 mt-1">
                          across {storageStats.gridfs_file_count} GridFS file
                          {storageStats.gridfs_file_count === 1 ? "" : "s"}
                        </p>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 rounded-xl">
                        <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">
                          Active packages
                        </p>
                        <p
                          className="text-2xl font-bold text-emerald-900 mt-1"
                          data-testid="storage-active-bytes"
                        >
                          {formatBytes(storageStats.metadata_active_bytes)}
                        </p>
                        <p className="text-xs text-emerald-700/70 mt-1">
                          {storageStats.active_package_count} active /{" "}
                          {storageStats.package_count} total
                        </p>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-slate-50 to-gray-50 border border-slate-100 rounded-xl">
                        <p className="text-xs uppercase tracking-wide text-slate-700 font-semibold">
                          Tracked metadata
                        </p>
                        <p className="text-2xl font-bold text-slate-900 mt-1">
                          {formatBytes(storageStats.metadata_total_bytes)}
                        </p>
                        <p className="text-xs text-slate-700/70 mt-1">
                          sum of package rows
                        </p>
                      </div>
                      <div
                        className={`p-4 border rounded-xl ${
                          storageStats.orphan_bytes > 0
                            ? "bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200"
                            : "bg-gradient-to-br from-gray-50 to-slate-50 border-gray-100"
                        }`}
                      >
                        <p
                          className={`text-xs uppercase tracking-wide font-semibold ${
                            storageStats.orphan_bytes > 0
                              ? "text-amber-700"
                              : "text-slate-600"
                          }`}
                        >
                          Orphaned bytes
                        </p>
                        <p
                          className={`text-2xl font-bold mt-1 ${
                            storageStats.orphan_bytes > 0
                              ? "text-amber-900"
                              : "text-slate-800"
                          }`}
                          data-testid="storage-orphan-bytes"
                        >
                          {formatBytes(storageStats.orphan_bytes)}
                        </p>
                        <p
                          className={`text-xs mt-1 ${
                            storageStats.orphan_bytes > 0
                              ? "text-amber-700/80"
                              : "text-slate-500"
                          }`}
                        >
                          {storageStats.orphan_bytes > 0
                            ? "GridFS files without a metadata row"
                            : "Nothing dangling"}
                        </p>
                        {storageStats.orphan_bytes > 0 && (
                          <button
                            type="button"
                            onClick={handlePruneOrphans}
                            data-testid="prune-orphans-btn"
                            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-600 hover:bg-amber-700 text-white transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Prune orphans
                          </button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Existing Packages */}
              <Card className="bg-white border border-gray-100 shadow-lg rounded-2xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-2xl">
                    📚 Uploaded Packages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {appPackages.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No packages uploaded yet</p>
                      <p className="text-sm mt-2">Upload your first web app package above</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {appPackages.map((pkg) => (
                        <div
                          key={pkg.id}
                          className={`bg-gray-50 border border-gray-200 p-4 hover:shadow-md transition-shadow ${
                            pkg.is_active ? 'border-green-300 bg-green-50' : 'border-gray-200'
                          }`}
                        >
                          <div className="flex justify-between items-start gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-semibold text-gray-800">{pkg.filename}</h3>
                                {pkg.is_active && (
                                  <span className="px-2 py-1 bg-green-600 text-white text-xs rounded-full font-semibold">
                                    ACTIVE
                                  </span>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                                <p><strong>Tier:</strong> {pkg.tier.toUpperCase()}</p>
                                <p><strong>Version:</strong> {pkg.version}</p>
                                <p><strong>Size:</strong> {(pkg.file_size / 1024 / 1024).toFixed(2)} MB</p>
                                <p><strong>Downloads:</strong> {pkg.download_count}</p>
                              </div>
                              <p className="text-xs text-gray-500 mt-2">
                                Uploaded: {new Date(pkg.uploaded_at).toLocaleString()}
                              </p>
                            </div>
                            <ActionTile
                              icon={Trash2}
                              tint={TINT.expenses}
                              onClick={() => handleDeletePackage(pkg.id)}
                              title="Delete package"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Help Card */}
              <Card className="bg-white border border-gray-100 shadow-lg rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-gray-800 mb-3">💡 Tips & Best Practices</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span>Include a README.md or DEPLOYMENT_GUIDE.md in your ZIP file</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span>Test the ZIP file locally before uploading (extract and verify contents)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span>Keep file size under 50MB for faster downloads</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span>Version your packages (e.g., v1.0.0, v1.0.1) for tracking</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span>Customers get download links automatically in license emails</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <Card className="bg-white border border-gray-100 shadow-lg rounded-2xl max-w-4xl mx-auto">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Shield className="w-6 h-6 text-blue-700" />
                  Security Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Account Information */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                      <User className="w-5 h-5 text-blue-700" />
                      Account Information
                    </h3>
                    {!editingAccount && (
                      <Button
                        onClick={() => setEditingAccount(true)}
                        variant="outline"
                        size="sm"
                        className="border-blue-300 text-blue-700 hover:bg-blue-50"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </Button>
                    )}
                  </div>
                  
                  {editingAccount ? (
                    <form onSubmit={handleUpdateAccount} className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="account-username">Username *</Label>
                          <Input
                            id="account-username"
                            type="text"
                            value={accountForm.username}
                            onChange={(e) => setAccountForm({ ...accountForm, username: e.target.value })}
                            required
                            className="mt-1 bg-white"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="account-email">Email Address *</Label>
                          <Input
                            id="account-email"
                            type="email"
                            value={accountForm.email}
                            onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })}
                            required
                            className="mt-1 bg-white"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="account-fullname">Full Name</Label>
                        <Input
                          id="account-fullname"
                          type="text"
                          value={accountForm.full_name}
                          onChange={(e) => setAccountForm({ ...accountForm, full_name: e.target.value })}
                          placeholder="Your full name"
                          className="mt-1 bg-white"
                        />
                      </div>
                      
                      <div className="flex gap-3 pt-2">
                        <button
                          type="submit"
                          disabled={loading}
                          className="px-6 py-2 text-white font-semibold transition-all duration-200"
                          style={{ backgroundColor: '#0073ea' }}
                          onMouseEnter={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#005bb5')}
                          onMouseLeave={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#0073ea')}
                        >
                          {loading ? "Saving..." : "Save Changes"}
                        </button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleCancelEdit}
                          disabled={loading}
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Username</p>
                        <p className="font-semibold text-gray-800">{adminUser?.username || 'N/A'}</p>
                      </div>
                      
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Email Address</p>
                        <p className="font-semibold text-gray-800">{adminUser?.email || 'N/A'}</p>
                      </div>
                      
                      {adminUser?.full_name && (
                        <div className="bg-white rounded-lg p-4 border border-gray-200 md:col-span-2">
                          <p className="text-xs text-gray-500 mb-1">Full Name</p>
                          <p className="font-semibold text-gray-800">{adminUser.full_name}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Two-Factor Authentication */}
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-800">Two-Factor Authentication</h3>
                        <span className={`badge ${twoFactorEnabled ? 'badge-active' : 'badge-inactive'}`}>
                          {twoFactorEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        {twoFactorEnabled 
                          ? 'Your account is protected with email verification codes. You will receive a 6-digit code each time you login.'
                          : 'Your account is not using two-factor authentication. Enable it for enhanced security.'}
                      </p>
                      <div className={`p-4 rounded-lg ${twoFactorEnabled ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                        <p className="text-sm font-semibold mb-2 ${twoFactorEnabled ? 'text-green-800' : 'text-amber-800'}">
                          {twoFactorEnabled ? '✓ Security Status: Protected' : '⚠️ Security Status: Standard'}
                        </p>
                        <ul className="text-xs space-y-1 ${twoFactorEnabled ? 'text-green-700' : 'text-amber-700'}">
                          {twoFactorEnabled ? (
                            <>
                              <li>• Email verification required for login</li>
                              <li>• Codes sent to: {adminUser?.email || 'your email'}</li>
                              <li>• Codes expire in 10 minutes</li>
                              <li>• Protection against unauthorized access</li>
                            </>
                          ) : (
                            <>
                              <li>• Only username and password required</li>
                              <li>• Faster login but less secure</li>
                              <li>• Recommended: Enable 2FA for better protection</li>
                            </>
                          )}
                        </ul>
                      </div>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <Switch
                        checked={twoFactorEnabled}
                        onCheckedChange={handleToggle2FA}
                        disabled={loading}
                        data-testid="2fa-toggle"
                      />
                      <span className="text-xs text-gray-500 text-center">
                        {twoFactorEnabled ? 'Turn Off' : 'Turn On'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Account Information */}
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Account Information</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Username</p>
                      <p className="font-semibold text-gray-800">{adminUser?.username}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-semibold text-gray-800">{adminUser?.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Role</p>
                      <p className="font-semibold text-gray-800 capitalize">{adminUser?.role}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">2FA Email</p>
                      <p className="font-semibold text-gray-800">{adminUser?.email || 'Not set'}</p>
                    </div>
                  </div>
                </div>

                {/* Change Password */}
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Change Password</h3>
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div>
                      <Label htmlFor="current-password">Current Password</Label>
                      <Input
                        id="current-password"
                        type="password"
                        value={passwordForm.current_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                        required
                        placeholder="Enter current password"
                        data-testid="current-password-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-password">New Password</Label>
                      <Input
                        id="new-password"
                        type="password"
                        value={passwordForm.new_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                        required
                        minLength={6}
                        placeholder="Enter new password (min 6 characters)"
                        data-testid="new-password-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="confirm-password">Confirm New Password</Label>
                      <Input
                        id="confirm-password"
                        type="password"
                        value={passwordForm.confirm_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                        required
                        placeholder="Confirm new password"
                        data-testid="confirm-password-input"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-6 py-2 text-white font-semibold transition-all duration-200"
                      data-testid="change-password-btn"
                      style={{ backgroundColor: '#0073ea' }}
                      onMouseEnter={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#005bb5')}
                      onMouseLeave={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#0073ea')}
                    >
                      {loading ? "Changing..." : "Change Password"}
                    </button>
                  </form>
                </div>

                {/* Update Email */}
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Update Admin Email</h3>
                  <form onSubmit={handleUpdateEmail} className="space-y-4">
                    <div>
                      <Label htmlFor="current-email">Current Email</Label>
                      <Input
                        id="current-email"
                        type="email"
                        value={adminUser?.email || ""}
                        disabled
                        className="bg-gray-50"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-email">New Email Address</Label>
                      <Input
                        id="new-email"
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        required
                        placeholder="Enter new email address"
                        data-testid="new-email-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Confirmation will be sent to both old and new email addresses
                      </p>
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-6 py-2 text-white font-semibold transition-all duration-200"
                      data-testid="update-email-btn"
                      style={{ backgroundColor: '#0073ea' }}
                      onMouseEnter={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#005bb5')}
                      onMouseLeave={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#0073ea')}
                    >
                      {loading ? "Updating..." : "Update Email"}
                    </button>
                  </form>
                </div>

                {/* Update 2FA Notification Email */}
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">2FA Notification Email</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    All two-factor authentication codes and security notifications are sent to:
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="font-mono text-blue-900 font-semibold">{adminUser?.email || 'Not configured'}</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    Codes will be sent to your registered email address. You can update your email in the Account Information section above.
                  </p>
                </div>

                {/* Security Recommendations */}
                <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                  <h3 className="text-lg font-semibold text-blue-900 mb-3">🛡️ Security Best Practices</h3>
                  <ul className="space-y-2 text-sm text-blue-800">
                    <li className="flex items-start gap-2">
                      <span className="mt-1">✓</span>
                      <span><strong>Keep 2FA Enabled:</strong> Adds an extra layer of security to your account</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1">✓</span>
                      <span><strong>Use Strong Password:</strong> Change default password immediately</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1">✓</span>
                      <span><strong>Monitor Email:</strong> Check your registered email regularly for security alerts and 2FA codes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1">✓</span>
                      <span><strong>Logout When Done:</strong> Always logout after finishing admin tasks</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
        </div>
      </div>

      {/* User Edit Modal */}
      {isUserModalOpen && (
        <UserEditModal
          user={selectedUser}
          onClose={() => {
            setIsUserModalOpen(false);
            setSelectedUser(null);
          }}
          onSave={handleSaveUser}
          loading={loading}
        />
      )}
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AdminDashboardSecure;